/**
 * Effective state over the peer → interface → connection chain (REQ-PEER-064 /
 * -065; REQ-UI-144).
 *
 * Pure, React-free.
 *
 * # Two facts, and showing either alone is a lie
 *
 * Stopping a peer suppresses emission for everything beneath it **without
 * overwriting** any descendant's recorded state (REQ-PEER-064). The suppression
 * is computed at read time and never written down, which is what makes an
 * operator's individual interface stop survive a peer stop/start cycle.
 *
 * So an entity has two states at once and the surface must show both:
 *
 *   - its **own** recorded state — what the operator set, and what it will
 *     return to when its ancestors start;
 *   - its **effective** state — whether it is emitting right now.
 *
 * Collapsing them would make a peer restart look like it silently changed
 * interfaces nobody touched, and would leave an operator unable to tell a flow
 * they stopped from one an ancestor is holding down.
 *
 * # Derived here, not served
 *
 * All three recorded states are in the all-roles read field-set (feature-spec
 * §2.10), so a non-privileged reader computes the identical annotation from the
 * identical facts and no new field crosses the RBAC split. This is deliberately
 * *not* the Peer Manager's `_effective_state` — that one governs the runtime and
 * reports through the status feed. This one describes the **configuration** the
 * dialog is showing, which is a different question asked of different data.
 *
 * Cross-references:
 *   - REQ-PEER-064 (non-destructive cascade), REQ-PEER-065 (effective state).
 *   - REQ-UI-144 (own state pill + `stopped by <ancestor>` annotation).
 *   - `packages/meridian_peer_manager/src/meridian_peer_manager/_effective_state.py`.
 */
import type { InterfaceState } from "@/peers/peer-catalog";

/** One level of the ancestor chain, root-first. */
export interface ChainLink {
  /** The entity's operator-facing name — what the annotation names. */
  name: string;
  /** Its own recorded state. */
  state: InterfaceState;
}

/** What to render for one entity: its own state, whether it emits, and why not. */
export interface EffectiveState {
  /** The entity's own recorded state — always what the state pill shows. */
  own: InterfaceState;
  /** ``started`` only when this entity and every ancestor are started. */
  effective: InterfaceState;
  /**
   * The **outermost** stopped ancestor's name, or ``null`` when no ancestor
   * suppresses this entity.
   *
   * Outermost rather than nearest, and the difference is the whole point: where
   * a peer and its interface are both stopped, starting the interface changes
   * nothing until the peer starts too. Naming the nearest ancestor would send an
   * operator to perform an act with no observable effect — the failure mode this
   * annotation exists to prevent.
   *
   * An entity's own ``stopped`` never appears here. "You stopped this" is
   * already carried by the state pill; repeating it as an annotation would make
   * a self-stop and an ancestor-stop read alike.
   */
  suppressedBy: string | null;
}

/**
 * Resolve one entity's effective state from its ancestor chain.
 *
 * @param ancestors Root-first, **excluding** the entity itself: `[]` for a peer,
 *   `[peer]` for an interface, `[peer, interface]` for a connection.
 * @param own The entity's own recorded state.
 * @returns Its own state, its effective state, and the suppressing ancestor.
 */
export function resolveEffectiveState(
  ancestors: readonly ChainLink[],
  own: InterfaceState,
): EffectiveState {
  const suppressor = ancestors.find((link) => link.state === "stopped") ?? null;
  return {
    own,
    // Fail-safe in the same direction the rest of this surface fails: anything
    // short of "this and every ancestor started" is not emitting.
    effective: suppressor === null && own === "started" ? "started" : "stopped",
    suppressedBy: suppressor?.name ?? null,
  };
}

/**
 * The operator-facing annotation, or ``null`` when nothing suppresses this entity.
 *
 * Kept beside the resolver so the copy has one definition; the components render
 * it verbatim rather than each assembling their own sentence.
 */
export function suppressionLabel(resolved: EffectiveState): string | null {
  return resolved.suppressedBy === null ? null : `Stopped by ${resolved.suppressedBy}`;
}
